import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  documentId,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  serverTimestamp,
  limit,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { db, functions, storage, studioDb } from "../firebase";
import { ACCOUNT_TYPES, ONBOARDING_STATUS } from "./profileService";
import {
  NON_PRIVATE_CAPSULE_TYPES,
  deleteTimeCapsule,
} from "./firestore/timeCapsules";

const BUSINESS_STATUS = {
  DRAFT: "draft",
  ACTIVE: "active",
};
const businessDb = studioDb;

const normalizeEmail = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
// What collaborators were called before the rename. Membership and invite
// documents written under the old name are still live, so every read path has
// to understand it. Exported so the backfill migration and the app agree on
// exactly one spelling of the legacy value.
export const LEGACY_COLLABORATOR_ROLE = "employee";

export const normalizeBusinessMembershipRole = (
  role,
  { fallback = "collaborator" } = {},
) => {
  const normalized = String(role || "")
    .trim()
    .toLowerCase();
  if (!normalized) return fallback;
  if (normalized === "owner") return "owner";
  if (normalized === "manager") return "manager";
  if (normalized === "collaborator") return "collaborator";
  // A legacy value is a RECOGNISED role, not an unknown one. Letting it reach
  // the fallback meant callers passing `fallback: ""` (createBusinessInvite,
  // respondToBusinessInvite) read it as "invalid role" and refused outright.
  if (normalized === LEGACY_COLLABORATOR_ROLE) return "collaborator";
  return fallback;
};
const getUserProfileRow = async (uid) => {
  const normalizedUid = String(uid || "").trim();
  if (!normalizedUid) return null;
  const userSnap = await getDoc(doc(db, "users", normalizedUid));
  if (!userSnap.exists()) return null;
  return { id: userSnap.id, ...(userSnap.data() || {}) };
};
const getUserAccountType = async (uid) => {
  const row = await getUserProfileRow(uid);
  return (
    String(row?.accountType || "")
      .trim()
      .toLowerCase() || null
  );
};
const getBusinessOwnerUid = async (businessId) => {
  const normalizedBusinessId = String(businessId || "").trim();
  if (!normalizedBusinessId) return null;
  const businessSnap = await getDoc(
    doc(businessDb, "businesses", normalizedBusinessId),
  );
  if (!businessSnap.exists()) return null;
  const business = businessSnap.data() || {};
  return (
    String(business?.ownerUid || business?.createdByUid || "").trim() || null
  );
};
const getActorBusinessRole = async ({
  businessId,
  uid,
  isAdminActor = false,
}) => {
  if (isAdminActor) return "admin";
  const normalizedBusinessId = String(businessId || "").trim();
  const normalizedUid = String(uid || "").trim();
  if (!normalizedBusinessId || !normalizedUid) return "";
  const ownerUid = await getBusinessOwnerUid(normalizedBusinessId);
  if (ownerUid && ownerUid === normalizedUid) return "owner";
  const membership = await getBusinessMembershipForUser({
    businessId: normalizedBusinessId,
    uid: normalizedUid,
  });
  return normalizeBusinessMembershipRole(membership?.role, { fallback: "" });
};
const assertOwnerAccountType = async ({ uid, isAdminActor = false }) => {
  if (isAdminActor) return;
  const accountType = await getUserAccountType(uid);
  if (accountType !== ACCOUNT_TYPES.BUSINESS) {
    throw new Error("Owners must use a business account.");
  }
};
const assertCanEditBusiness = async ({
  businessId,
  actorUid,
  isAdminActor = false,
}) => {
  const role = await getActorBusinessRole({
    businessId,
    uid: actorUid,
    isAdminActor,
  });
  if (role === "admin" || role === "owner" || role === "manager") return role;
  throw new Error("You do not have permission to manage this business.");
};
const assertCanDeleteBusiness = async ({
  businessId,
  actorUid,
  isAdminActor = false,
}) => {
  const role = await getActorBusinessRole({
    businessId,
    uid: actorUid,
    isAdminActor,
  });
  if (role === "admin" || role === "owner") return role;
  throw new Error("Only business owners or admins can delete this business.");
};
const assertCanManageTeamTarget = async ({
  businessId,
  actorUid,
  isAdminActor = false,
  targetRole = "collaborator",
}) => {
  const actorRole = await getActorBusinessRole({
    businessId,
    uid: actorUid,
    isAdminActor,
  });
  const normalizedTargetRole = normalizeBusinessMembershipRole(targetRole, {
    fallback: "",
  });
  if (!normalizedTargetRole) {
    throw new Error("Invalid team role.");
  }
  if (actorRole === "admin" || actorRole === "owner") return actorRole;
  if (actorRole === "manager" && normalizedTargetRole === "collaborator")
    return actorRole;
  throw new Error("Managers can only manage collaborators.");
};
/**
 * Who to credit in the modification trail. The auditBusinessUpdate and
 * auditBusinessMembership triggers run without auth context, so the uid has to
 * travel on the document itself — the same way softDeleteBusiness leaves
 * deletedByUid. Null rather than a guess when the caller has no uid: an
 * unattributed entry is honest, a wrong one is not.
 */
const auditActorUid = (value) => String(value || "").trim() || null;

const normalizeEmployeeScopes = (scopes = {}, { requireOne = false } = {}) => {
  const normalized = {
    tv: Boolean(scopes?.tv),
    timeCapsules: Boolean(scopes?.timeCapsules),
  };
  if (!normalized.tv && !normalized.timeCapsules) {
    if (requireOne) {
      throw new Error(
        "At least one access scope is required (Inssa Live or Time Capsules).",
      );
    }
    return { tv: true, timeCapsules: false };
  }
  return normalized;
};

const toDraftBusinessName = (businessId = "") => {
  const shortId = String(businessId || "")
    .trim()
    .slice(0, 6)
    .toUpperCase();
  return shortId ? `Draft Business ${shortId}` : "Draft Business";
};

export const resolveBusinessDisplayName = (business = {}) => {
  const companyName = String(business?.companyName || "").trim();
  if (companyName) return companyName;
  const explicitDisplayName = String(business?.displayName || "").trim();
  if (explicitDisplayName) return explicitDisplayName;
  return toDraftBusinessName(business?.id);
};

const toStringArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((v) => String(v || "").trim()).filter(Boolean);
  }
  return [];
};

const dedupeCI = (values) => {
  const seen = new Set();
  const out = [];
  values.forEach((v) => {
    const key = String(v || "").toLowerCase();
    if (key && !seen.has(key)) {
      seen.add(key);
      out.push(v);
    }
  });
  return out;
};

const dedupeQueryValues = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );

const normalizeWebsiteUrl = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const withProtocol = /^https?:\/\//i.test(trimmed)
    ? trimmed.replace(/^http:\/\//i, "https://")
    : `https://${trimmed}`;
  try {
    const parsed = new URL(withProtocol);
    parsed.protocol = "https:";
    return parsed.toString().replace(/\/$/, "");
  } catch {
    return withProtocol;
  }
};

export const updateTimeCapsuleBusinessWebsiteLinks = async ({
  existing = null,
  saved = null,
} = {}) => {
  const businessId = String(saved?.id || existing?.id || "").trim();
  const nextUrl = String(saved?.websiteUrl || "").trim();
  if (!businessId || !nextUrl) return { updatedCount: 0 };

  const oldUrls = dedupeQueryValues([existing?.websiteUrl]).filter(
    (value) => value !== nextUrl,
  );
  if (!oldUrls.length && String(existing?.websiteUrl || "").trim() === nextUrl) {
    return { updatedCount: 0 };
  }

  const capsuleDocsById = new Map();
  const queryValues = [
    { field: "callToActionBusinessId", value: businessId },
    ...oldUrls.map((value) => ({ field: "callToActionUrl", value })),
  ];

  await Promise.all(
    queryValues.map(async ({ field, value }) => {
      const snaps = await getDocs(
        query(
          collection(db, "timeCapsules"),
          where(field, "==", value),
          // Proved statically, with no business-membership branch to lean on:
          // a lookup pinned only by the business link is denied for every
          // non-admin. A capsule carrying a business call-to-action is
          // studio-authored and never private, so this changes no results and
          // only makes that invariant provable.
          where("capsuleType", "in", NON_PRIVATE_CAPSULE_TYPES),
        ),
      );
      snaps.docs.forEach((docSnap) => {
        const row = docSnap.data?.() || {};
        if (
          field === "callToActionBusinessId" &&
          String(row?.callToActionDestinationType || "").trim() !==
            "businessWebsite"
        ) {
          return;
        }
        capsuleDocsById.set(docSnap.id, docSnap);
      });
    }),
  );

  if (capsuleDocsById.size === 0) return { updatedCount: 0 };

  const batch = writeBatch(db);
  capsuleDocsById.forEach((docSnap) => {
    batch.update(doc(db, "timeCapsules", docSnap.id), {
      callToActionUrl: nextUrl,
      callToActionBusinessId: businessId,
      callToActionDestinationType: "businessWebsite",
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
  return { updatedCount: capsuleDocsById.size };
};

const normalizeBusinessInput = (raw = {}) => ({
  companyName: String(raw.companyName || "").trim(),
  tagline: String(raw.tagline || "").trim(),
  corporationId: String(raw.corporationId || "").trim() || null,
  businessCategories: toStringArray(raw.businessCategories),
  hqAddress: String(raw.hqAddress || "").trim(),
  hqPlaceId: String(raw.hqPlaceId || "").trim() || null,
  hqAddressComponents: Array.isArray(raw.hqAddressComponents)
    ? raw.hqAddressComponents
    : [],
  hqLocation:
    raw?.hqLocation &&
    Number.isFinite(Number(raw.hqLocation.lat)) &&
    Number.isFinite(Number(raw.hqLocation.lng))
      ? {
          lat: Number(raw.hqLocation.lat),
          lng: Number(raw.hqLocation.lng),
          geohash: String(raw.hqLocation.geohash || "").trim() || null,
        }
      : null,
  locations: toStringArray(raw.locations),
  locationDetails: Array.isArray(raw.locationDetails)
    ? raw.locationDetails
        .map((entry) => {
          const address = String(entry?.address || "").trim();
          const lat = Number(entry?.lat);
          const lng = Number(entry?.lng);
          if (!address) return null;
          return {
            address,
            placeId: String(entry?.placeId || "").trim() || null,
            addressComponents: Array.isArray(entry?.addressComponents)
              ? entry.addressComponents
              : [],
            ...(Number.isFinite(lat) && Number.isFinite(lng)
              ? {
                  lat,
                  lng,
                  geohash: String(entry?.geohash || "").trim() || null,
                }
              : {}),
          };
        })
        .filter(Boolean)
    : [],
  // Unified location list (source of truth in the wizard). The legacy
  // hq*/locations/locationDetails above are derived from it before save.
  locationEntries: Array.isArray(raw.locationEntries)
    ? raw.locationEntries
        .map((entry) => {
          const label = String(entry?.label || entry?.address || "").trim();
          const address = String(entry?.address || entry?.label || "").trim();
          if (!label && !address) return null;
          const lat = Number(entry?.lat);
          const lng = Number(entry?.lng);
          return {
            id: String(entry?.id || "").trim() || null,
            label,
            address,
            // A business can hold several distinct spaces at one venue address
            // (airport concourses, mall units, market stalls); these keep them
            // apart. Already normalized upstream by createEntry.
            venueName: String(entry?.venueName || "").trim(),
            unitLabel: String(entry?.unitLabel || "").trim(),
            unitType: String(entry?.unitType || "").trim().toLowerCase(),
            placeId: String(entry?.placeId || "").trim() || null,
            addressComponents: Array.isArray(entry?.addressComponents)
              ? entry.addressComponents
              : [],
            isHeadquarters: Boolean(entry?.isHeadquarters),
            source: entry?.source === "pin" ? "pin" : "search",
            ...(Number.isFinite(lat) && Number.isFinite(lng)
              ? { lat, lng, geohash: String(entry?.geohash || "").trim() || null }
              : {}),
          };
        })
        .filter(Boolean)
    : [],
  hashtags: toStringArray(raw.hashtags),
  websiteUrl: normalizeWebsiteUrl(raw.websiteUrl || raw.businessWebsiteUrl || ""),
  approxEmployeeCount: String(raw.approxEmployeeCount || "").trim(),
  marketingCampaignDescription: String(
    raw.marketingCampaignDescription || "",
  ).trim(),
  adRestrictions: {
    enabled:
      raw?.adRestrictions?.enabled === undefined
        ? false
        : Boolean(raw.adRestrictions.enabled),
    categories: dedupeCI(toStringArray(raw?.adRestrictions?.categories)),
    blockedKeywords: dedupeCI(
      toStringArray(raw?.adRestrictions?.blockedKeywords).map((v) =>
        v.toLowerCase(),
      ),
    ),
  },
  avatarUrl: String(raw.avatarUrl || "").trim() || null,
  avatarStoragePath: String(raw.avatarStoragePath || "").trim() || null,
  pinUrl: String(raw.pinUrl || "").trim() || null,
  pinStoragePath: String(raw.pinStoragePath || "").trim() || null,
});

const toTokenParts = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .map((v) => v.trim())
    .filter(Boolean);

const buildBusinessSearchTokens = (business = {}) => {
  const seedValues = [
    business.companyName,
    business.tagline,
    business.corporationId,
    business.hqAddress,
    business.websiteUrl,
    ...(Array.isArray(business.businessCategories)
      ? business.businessCategories
      : []),
    ...(Array.isArray(business.locations) ? business.locations : []),
    ...(Array.isArray(business.hashtags) ? business.hashtags : []),
  ];

  const tokens = new Set();
  seedValues.forEach((value) => {
    toTokenParts(value).forEach((token) => {
      if (!token) return;
      tokens.add(token);
      const maxPrefixLen = Math.min(token.length, 12);
      for (let i = 3; i <= maxPrefixLen; i += 1) {
        tokens.add(token.slice(0, i));
      }
    });
  });

  return Array.from(tokens);
};

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") {
    return (
      value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1000000)
    );
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const isPermissionDeniedError = (error) =>
  String(error?.code || "")
    .toLowerCase()
    .includes("permission-denied");

const safeDeleteStoragePath = async (path) => {
  const normalizedPath = String(path || "").trim();
  if (!normalizedPath) return;
  try {
    await deleteObject(ref(storage, normalizedPath));
  } catch (error) {
    const code = String(error?.code || "").toLowerCase();
    if (!code.includes("object-not-found")) {
      throw error;
    }
  }
};

const archiveBusinessCampaigns = async ({ businessId, uid }) => {
  const normalizedBusinessId = String(businessId || "").trim();
  if (!normalizedBusinessId) return;
  try {
    const campaignsSnap = await getDocs(
      query(
        collection(studioDb, "raffles"),
        where("businessId", "==", normalizedBusinessId),
      ),
    );
    for (const campaignDoc of campaignsSnap.docs) {
      try {
        await updateDoc(campaignDoc.ref, {
          status: "archived",
          archivedAt: serverTimestamp(),
          archivedByUid: uid,
          archiveReason: "business_deleted",
          updatedAt: serverTimestamp(),
          updatedAtMs: Date.now(),
          updatedBy: uid,
        });
      } catch (error) {
        if (!isPermissionDeniedError(error)) throw error;
        console.warn(
          "Skipping campaign archive due to permissions:",
          campaignDoc.id,
        );
      }
    }
  } catch (error) {
    if (!isPermissionDeniedError(error)) throw error;
    console.warn("Skipping campaign archive lookup due to permissions.");
  }
};

const deactivateBusinessTvDevices = async ({ businessId, uid }) => {
  const normalizedBusinessId = String(businessId || "").trim();
  if (!normalizedBusinessId) return;
  try {
    const devicesSnap = await getDocs(
      query(
        collection(studioDb, "tvDevices"),
        where("businessId", "==", normalizedBusinessId),
      ),
    );
    for (const deviceDoc of devicesSnap.docs) {
      try {
        await updateDoc(deviceDoc.ref, {
          status: "inactive",
          deactivatedAt: serverTimestamp(),
          deactivatedByUid: uid,
          deactivationReason: "business_deleted",
          playlistCollectionId: null,
          playlistCollectionName: null,
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        if (!isPermissionDeniedError(error)) throw error;
        console.warn(
          "Skipping TV device deactivation due to permissions:",
          deviceDoc.id,
        );
      }
    }
  } catch (error) {
    if (!isPermissionDeniedError(error)) throw error;
    console.warn("Skipping TV device lookup due to permissions.");
  }
};

const deactivateBusinessLocations = async ({ businessId, uid }) => {
  const normalizedBusinessId = String(businessId || "").trim();
  if (!normalizedBusinessId) return;
  try {
    const locationsSnap = await getDocs(
      query(
        collection(studioDb, "businessLocations"),
        where("businessId", "==", normalizedBusinessId),
      ),
    );
    for (const locationDoc of locationsSnap.docs) {
      try {
        await updateDoc(locationDoc.ref, {
          status: "inactive",
          deactivatedAt: serverTimestamp(),
          deactivatedByUid: uid,
          deactivationReason: "business_deleted",
          playlistCollectionId: null,
          playlistCollectionName: null,
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        if (!isPermissionDeniedError(error)) throw error;
        console.warn(
          "Skipping business location deactivation due to permissions:",
          locationDoc.id,
        );
      }
    }
  } catch (error) {
    if (!isPermissionDeniedError(error)) throw error;
    console.warn("Skipping business location lookup due to permissions.");
  }
};

const getStudioCollectionById = async (collectionId) => {
  const normalized = String(collectionId || "").trim();
  if (!normalized) return null;
  const snap = await getDoc(doc(studioDb, "collections", normalized));
  return snap.exists() ? { id: snap.id, ...(snap.data() || {}) } : null;
};

const getDefaultFallbackCollection = async () => {
  const flaggedSnaps = await getDocs(
    query(
      collection(studioDb, "collections"),
      where("isDefaultFallback", "==", true),
      limit(5),
    ),
  );
  const rows = flaggedSnaps.docs.map((d) => ({
    id: d.id,
    ...(d.data() || {}),
  }));
  if (rows.length === 0) {
    const [namedSnaps, legacyNamedSnaps] = await Promise.all([
      getDocs(
        query(
          collection(studioDb, "collections"),
          where("name", "==", "Default Fallback Playlist"),
          limit(5),
        ),
      ),
      getDocs(
        query(
          collection(studioDb, "collections"),
          where("name", "==", "Default Fallback Collection"),
          limit(5),
        ),
      ),
    ]);
    namedSnaps.docs.forEach((d) =>
      rows.push({ id: d.id, ...(d.data() || {}) }),
    );
    legacyNamedSnaps.docs.forEach((d) =>
      rows.push({ id: d.id, ...(d.data() || {}) }),
    );
  }
  if (rows.length === 0) return null;
  const published = rows.filter(
    (row) => String(row?.publishStatus || "published") === "published",
  );
  const source = published.length > 0 ? published : rows;
  source.sort(
    (a, b) =>
      toMillis(b.updatedAt || b.createdAt) -
      toMillis(a.updatedAt || a.createdAt),
  );
  return source[0] || null;
};

const getLatestPublishedBusinessOwnedCollection = async (
  business = {},
  excludedCollectionId = null,
) => {
  const ownerUid = String(
    business?.ownerUid || business?.createdByUid || "",
  ).trim();
  if (!ownerUid) return null;
  const snaps = await getDocs(
    query(
      collection(studioDb, "collections"),
      where("createdBy", "==", ownerUid),
      limit(100),
    ),
  );
  if (snaps.empty) return null;
  const excluded = String(excludedCollectionId || "").trim();
  const rows = snaps.docs
    .map((d) => ({ id: d.id, ...(d.data() || {}) }))
    .filter((c) => String(c?.publishStatus || "published") === "published")
    .filter((c) => String(c?.id || "").trim() !== excluded);
  rows.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
  return rows[0] || null;
};

const resolveBusinessFallbackCollection = async (
  business = {},
  excludedCollectionId = null,
) => {
  const owned = await getLatestPublishedBusinessOwnedCollection(
    business,
    excludedCollectionId,
  );
  if (owned) {
    return {
      id: owned.id,
      name: owned.name || null,
      isDefaultFallback: Boolean(owned.isDefaultFallback),
      publishStatus: String(owned.publishStatus || "published"),
    };
  }
  const fallback = await getDefaultFallbackCollection();
  if (!fallback) return null;
  return {
    id: fallback.id,
    name: fallback.name || "Default Fallback Playlist",
    isDefaultFallback: true,
    publishStatus: String(fallback.publishStatus || "published"),
  };
};

const isCollectionDefaultFallback = async (collectionId) => {
  const collectionDoc = await getStudioCollectionById(collectionId);
  return Boolean(collectionDoc?.isDefaultFallback);
};

export const getBusinessById = async (businessId) => {
  if (!businessId) return null;
  const ref = doc(businessDb, "businesses", businessId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

const assertUniqueBusinessName = async (
  companyName,
  { excludeBusinessId = null } = {},
) => {
  const normalizedName = String(companyName || "").trim();
  if (!normalizedName) return;
  const normalizedExclude = String(excludeBusinessId || "").trim();
  // Global name-uniqueness needs to read every business, which non-admins can
  // no longer do under the member-scoped security rules. Treat it as a
  // best-effort UX check: if listing all businesses is denied, skip it rather
  // than block the save. (Enforce uniqueness server-side if it must be hard.)
  let allBusinesses = [];
  try {
    allBusinesses = await listAllBusinesses();
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      console.warn(
        "Skipping business name uniqueness check (cannot list all businesses).",
      );
      return;
    }
    throw error;
  }
  const duplicate = (Array.isArray(allBusinesses) ? allBusinesses : []).find(
    (business) => {
      const businessId = String(business?.id || "").trim();
      if (normalizedExclude && businessId === normalizedExclude) return false;
      return (
        String(business?.companyName || "")
          .trim()
          .toLowerCase() === normalizedName.toLowerCase()
      );
    },
  );
  if (duplicate) {
    throw new Error(
      "A business with that company name already exists. Please use a unique name.",
    );
  }
};

export const subscribeBusinessById = (businessId, onNext, onError) => {
  if (!businessId) {
    onNext?.(null);
    return () => {};
  }
  return onSnapshot(
    doc(businessDb, "businesses", businessId),
    (snap) => {
      onNext?.(snap.exists() ? { id: snap.id, ...(snap.data() || {}) } : null);
    },
    (error) => {
      onError?.(error);
    },
  );
};

/**
 * The inverse of subscribeAllBusinesses, which filters deleted rows out at the
 * source. Admin-only by use: an owner whose business was deleted has lost
 * access to it by definition.
 */
export const subscribeDeletedBusinesses = (onNext, onError) => {
  return onSnapshot(
    collection(businessDb, "businesses"),
    (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((row) => row?.status === "deleted")
        .sort((a, b) =>
          resolveBusinessDisplayName(a).localeCompare(
            resolveBusinessDisplayName(b),
          ),
        );
      onNext?.(rows);
    },
    (error) => onError?.(error),
  );
};

export const subscribeAllBusinesses = (onNext, onError) => {
  return onSnapshot(
    collection(businessDb, "businesses"),
    (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((row) => row?.status !== "deleted")
        .sort((a, b) =>
          resolveBusinessDisplayName(a).localeCompare(
            resolveBusinessDisplayName(b),
          ),
        );
      onNext?.(rows);
    },
    (error) => {
      onError?.(error);
    },
  );
};

export const subscribeBusinessesForUser = (uid, onNext, onError) => {
  if (!uid) {
    onNext?.([]);
    return () => {};
  }

  let cancelled = false;
  // Whether the memberships stream has delivered at least once. Until it has we
  // can't tell a roster business the viewer genuinely belongs to from a stale
  // roster entry, so we don't apply the active-membership filter yet (avoids a
  // "No business found" flash for a member on first load).
  let membershipsLoaded = false;
  const businessById = new Map();
  const membershipRoleByBusinessId = new Map();
  // Businesses fetched by id because they have a membership doc but are missing
  // from the denormalized memberUids roster query (see reconcileFallback below).
  const fallbackBusinessById = new Map();
  // Ids we've already attempted to fetch as a fallback, so a doc that resolves to
  // nothing (deleted/unreadable) doesn't trigger an endless refetch loop. Cleared
  // for an id only when its backing membership disappears.
  const fallbackAttempted = new Set();

  // The denormalized memberUids roster lags membership writes, so a business the
  // viewer just LEFT (their membership is now inactive) can still come back from
  // the array-contains query for a few seconds. Only surface a roster business
  // the viewer actually belongs to — has an active membership for, or owns — so a
  // left business disappears immediately instead of getting re-selected as the
  // active business by the hub/app-bar. Skipped until memberships have loaded.
  const belongsToViewer = (row, id) => {
    if (!membershipsLoaded) return true;
    if (membershipRoleByBusinessId.has(id)) return true;
    const ownerUid = String(row?.ownerUid || "").trim();
    const createdByUid = String(row?.createdByUid || "").trim();
    return ownerUid === uid || createdByUid === uid;
  };

  const emit = () => {
    const merged = new Map();
    // Fallback businesses first; the roster query wins on any id collision.
    fallbackBusinessById.forEach((row, id) => {
      if (businessById.has(id)) return;
      if (!membershipRoleByBusinessId.has(id)) return;
      merged.set(id, row);
    });
    businessById.forEach((row, id) => {
      if (!belongsToViewer(row, id)) return;
      merged.set(id, row);
    });
    const rows = Array.from(merged.values())
      .filter((row) => row?.status !== "deleted")
      .map((row) => {
        const role = membershipRoleByBusinessId.get(String(row?.id || "").trim());
        return role ? { ...row, viewerMembershipRole: role } : row;
      })
      .sort((a, b) =>
        resolveBusinessDisplayName(a).localeCompare(
          resolveBusinessDisplayName(b),
        ),
      );
    onNext?.(rows);
  };

  // The memberUids roster is denormalized by a server-side trigger, so a freshly
  // accepted invite's (or just-created business's) membership doc can exist
  // before the roster catches up — and the roster can drift out of sync. Fetch
  // any membership business missing from the roster query directly by id so it
  // shows up here, mirroring listBusinessesForUser. Without this the Business Hub
  // renders "No business found" while the app bar (which uses
  // listBusinessesForUser) still shows the business — a confusing split-brain.
  const reconcileFallback = () => {
    // Forget fallback state for memberships that no longer exist.
    Array.from(fallbackBusinessById.keys()).forEach((id) => {
      if (!membershipRoleByBusinessId.has(id)) fallbackBusinessById.delete(id);
    });
    Array.from(fallbackAttempted).forEach((id) => {
      if (!membershipRoleByBusinessId.has(id)) fallbackAttempted.delete(id);
    });

    const missingIds = Array.from(membershipRoleByBusinessId.keys()).filter((id) => {
      const normalizedId = String(id || "").trim();
      return (
        normalizedId &&
        !businessById.has(normalizedId) &&
        !fallbackAttempted.has(normalizedId)
      );
    });
    if (missingIds.length === 0) return;
    missingIds.forEach((id) => fallbackAttempted.add(id));

    const CHUNK = 10;
    for (let i = 0; i < missingIds.length; i += CHUNK) {
      const slice = missingIds.slice(i, i + CHUNK);
      if (slice.length > 0) fetchFallbackChunk(slice);
    }
  };

  const fetchFallbackChunk = (slice) => {
    getDocs(
      query(
        collection(businessDb, "businesses"),
        where(documentId(), "in", slice),
      ),
    )
      .then((missingSnap) => {
        if (cancelled) return;
        let changed = false;
        missingSnap.forEach((docSnap) => {
          fallbackBusinessById.set(docSnap.id, {
            id: docSnap.id,
            ...(docSnap.data() || {}),
          });
          changed = true;
        });
        if (changed) emit();
      })
      .catch((error) => {
        if (cancelled) return;
        onError?.(error);
      });
  };

  // Read the businesses this user belongs to directly off the denormalized
  // memberUids roster. This is a single indexed query (array-contains) that the
  // security rules can authorize per-doc, replacing the membership -> chunked
  // documentId() "in" fan-out.
  const businessesUnsub = onSnapshot(
    query(
      collection(businessDb, "businesses"),
      where("memberUids", "array-contains", uid),
    ),
    (snap) => {
      businessById.clear();
      snap.forEach((docSnap) => {
        businessById.set(docSnap.id, {
          id: docSnap.id,
          ...(docSnap.data() || {}),
        });
      });
      reconcileFallback();
      emit();
    },
    (error) => {
      onError?.(error);
    },
  );

  // Memberships drive the viewer's role label per business and back the
  // roster-lag fallback above.
  const membershipsUnsub = onSnapshot(
    query(
      collection(businessDb, "businessMemberships"),
      where("uid", "==", uid),
    ),
    (membershipsSnapshot) => {
      membershipRoleByBusinessId.clear();
      membershipsSnapshot.docs
        .map((d) => d.data())
        .filter((row) => row?.status !== "inactive")
        .forEach((row) => {
          const businessId = String(row?.businessId || "").trim();
          if (!businessId) return;
          membershipRoleByBusinessId.set(
            businessId,
            normalizeBusinessMembershipRole(row?.role),
          );
        });
      membershipsLoaded = true;
      reconcileFallback();
      emit();
    },
    (error) => {
      onError?.(error);
    },
  );

  return () => {
    cancelled = true;
    if (typeof businessesUnsub === "function") businessesUnsub();
    if (typeof membershipsUnsub === "function") membershipsUnsub();
  };
};

export const listBusinessesForUser = async (uid) => {
  if (!uid) return [];

  const membershipsQuery = query(
    collection(businessDb, "businessMemberships"),
    where("uid", "==", uid),
  );
  const membershipsSnapshot = await getDocs(membershipsQuery);
  const membershipRoleByBusinessId = new Map();
  membershipsSnapshot.docs
    .map((d) => d.data())
    .filter((row) => row?.status !== "inactive")
    .forEach((row) => {
      const businessId = String(row?.businessId || "").trim();
      if (!businessId) return;
      membershipRoleByBusinessId.set(
        businessId,
        normalizeBusinessMembershipRole(row?.role),
      );
    });
  // Read businesses directly off the denormalized memberUids roster (single
  // indexed array-contains query) instead of a chunked documentId() "in" fan-out.
  const businessesSnap = await getDocs(
    query(
      collection(businessDb, "businesses"),
      where("memberUids", "array-contains", uid),
    ),
  );
  const results = [];
  const seenIds = new Set();
  const pushBusiness = (id, data = {}) => {
    const normalizedId = String(id || "").trim();
    if (!normalizedId || seenIds.has(normalizedId)) return;
    if (data?.status === "deleted") return;
    seenIds.add(normalizedId);
    const role = membershipRoleByBusinessId.get(normalizedId);
    results.push({
      id: normalizedId,
      ...data,
      ...(role ? { viewerMembershipRole: role } : {}),
    });
  };
  // Only surface a roster business the viewer actually belongs to — has an
  // active membership for, or owns/created. The memberUids roster lags
  // membership writes, so a business the viewer just LEFT can still come back
  // from the array-contains query for a few seconds; without this filter the
  // app-bar switcher would re-select it as the active business after a leave.
  const belongsToViewer = (data = {}, id = "") => {
    if (membershipRoleByBusinessId.has(id)) return true;
    const ownerUid = String(data?.ownerUid || "").trim();
    const createdByUid = String(data?.createdByUid || "").trim();
    return ownerUid === uid || createdByUid === uid;
  };
  businessesSnap.forEach((d) => {
    const data = d.data() || {};
    if (!belongsToViewer(data, d.id)) return;
    pushBusiness(d.id, data);
  });

  // The memberUids roster is denormalized by a server-side trigger, so a
  // freshly accepted invite's membership doc exists before the roster catches
  // up. Fetch any membership businesses missing from the roster query directly
  // by id so a just-joined business shows up immediately (e.g. right after
  // accepting an invite) instead of after the trigger propagates.
  const missingIds = Array.from(membershipRoleByBusinessId.keys()).filter(
    (id) => !seenIds.has(String(id || "").trim()),
  );
  const CHUNK = 10;
  for (let i = 0; i < missingIds.length; i += CHUNK) {
    const slice = missingIds.slice(i, i + CHUNK);
    if (slice.length === 0) continue;
    const missingSnap = await getDocs(
      query(
        collection(businessDb, "businesses"),
        where(documentId(), "in", slice),
      ),
    );
    missingSnap.forEach((d) => pushBusiness(d.id, d.data() || {}));
  }

  return results.sort((a, b) =>
    resolveBusinessDisplayName(a).localeCompare(resolveBusinessDisplayName(b)),
  );
};

/**
 * The businesses this user actually owns, as opposed to belongs to.
 *
 * Deliberately not derived from `primaryBusinessId`: that field is written to
 * mean "the business I am currently viewing" (the Hub switcher and
 * refreshBusinesses both persist it), so reading it as "the business I own"
 * targets a collaborator's business. See issue #5.
 */
export const listOwnedBusinessesForUser = async (uid) => {
  const normalizedUid = String(uid || "").trim();
  if (!normalizedUid) return [];
  const rows = await listBusinessesForUser(normalizedUid);
  return rows.filter((row) => {
    const ownerUid = String(row?.ownerUid || "").trim();
    const createdByUid = String(row?.createdByUid || "").trim();
    return ownerUid === normalizedUid || createdByUid === normalizedUid;
  });
};

export const listAllBusinesses = async () => {
  const snap = await getDocs(collection(businessDb, "businesses"));
  const rows = snap.docs
    .map((d) => ({ id: d.id, ...(d.data() || {}) }))
    .filter((row) => row?.status !== "deleted");
  return rows.sort((a, b) =>
    resolveBusinessDisplayName(a).localeCompare(resolveBusinessDisplayName(b)),
  );
};

export const backfillBusinessSearchTokens = async () => {
  const snap = await getDocs(collection(businessDb, "businesses"));
  const batch = writeBatch(businessDb);
  let touched = 0;

  snap.forEach((docSnap) => {
    const data = docSnap.data() || {};
    if (data?.status === "deleted") return;
    const normalized = normalizeBusinessInput(data);
    const nextTokens = buildBusinessSearchTokens(normalized);
    const currentTokens = Array.isArray(data.searchTokens)
      ? data.searchTokens.map((v) => String(v || "")).sort()
      : [];
    const nextSorted = [...nextTokens].sort();
    const unchanged =
      currentTokens.length === nextSorted.length &&
      currentTokens.every((v, i) => v === nextSorted[i]);
    if (unchanged) return;

    batch.set(
      doc(businessDb, "businesses", docSnap.id),
      { searchTokens: nextTokens, updatedAt: serverTimestamp() },
      { merge: true },
    );
    touched += 1;
  });

  if (touched > 0) {
    await batch.commit();
  }
  return { touched };
};

// One-time migration: populate the denormalized memberUids[] roster on every
// business doc from its active memberships (plus owner/creator), so the
// member-scoped security rules and array-contains reads work for businesses
// created before memberUids existed. Run this as an admin BEFORE deploying the
// tightened firestore.studio.rules. Idempotent — only writes when changed.
export const backfillBusinessMemberUids = async () => {
  const [businessesSnap, membershipsSnap] = await Promise.all([
    getDocs(collection(businessDb, "businesses")),
    getDocs(collection(businessDb, "businessMemberships")),
  ]);

  const memberUidsByBusinessId = new Map();
  membershipsSnap.forEach((docSnap) => {
    const data = docSnap.data() || {};
    if (String(data?.status || "active").toLowerCase() === "inactive") return;
    const businessId = String(data?.businessId || "").trim();
    const uid = String(data?.uid || "").trim();
    if (!businessId || !uid) return;
    if (!memberUidsByBusinessId.has(businessId)) {
      memberUidsByBusinessId.set(businessId, new Set());
    }
    memberUidsByBusinessId.get(businessId).add(uid);
  });

  let batch = writeBatch(businessDb);
  let pending = 0;
  let touched = 0;

  // eslint-disable-next-line no-restricted-syntax
  for (const docSnap of businessesSnap.docs) {
    const data = docSnap.data() || {};
    if (data?.status === "deleted") continue;
    const roster = memberUidsByBusinessId.get(docSnap.id) || new Set();
    const ownerUid = String(data?.ownerUid || "").trim();
    const createdByUid = String(data?.createdByUid || "").trim();
    if (ownerUid) roster.add(ownerUid);
    if (createdByUid) roster.add(createdByUid);

    const nextMemberUids = Array.from(roster).sort();
    const currentMemberUids = Array.isArray(data?.memberUids)
      ? data.memberUids.map((v) => String(v || "").trim()).filter(Boolean).sort()
      : [];
    const unchanged =
      currentMemberUids.length === nextMemberUids.length &&
      currentMemberUids.every((v, i) => v === nextMemberUids[i]);
    if (unchanged) continue;

    batch.set(
      doc(businessDb, "businesses", docSnap.id),
      { memberUids: nextMemberUids, updatedAt: serverTimestamp() },
      { merge: true },
    );
    touched += 1;
    pending += 1;
    // Firestore batches cap at 500 writes; commit in chunks.
    if (pending >= 400) {
      await batch.commit();
      batch = writeBatch(businessDb);
      pending = 0;
    }
  }

  if (pending > 0) {
    await batch.commit();
  }
  return { touched, total: businessesSnap.size };
};

const backfillBusinessMemberIndexCallable = httpsCallable(
  functions,
  "backfillBusinessMemberIndex"
);

/**
 * Drive the paged businessMemberIndex backfill to completion.
 *
 * This mirrors the authoritative memberUids roster (backfillBusinessMemberUids,
 * above — a different operation, in the studio database) into
 * businessMemberIndex in the default database, which is what the timeCapsules
 * security rules read. Must be run once per environment before those rules go
 * live, or existing members are locked out of their businesses' capsules.
 *
 * One callable invocation per page, following the returned cursor. Safe to
 * re-run: every write is a merge of a sorted, deduplicated array, so a
 * redundant write is a no-op on read. A failure mid-run propagates, and
 * re-running resumes from the start at the cost of reads and idempotent
 * writes only.
 */
export async function backfillBusinessMemberIndex({ onProgress } = {}) {
  let cursor = null;
  let processed = 0;
  let written = 0;
  let pages = 0;

  for (;;) {
    const response = await backfillBusinessMemberIndexCallable({ cursor, pageSize: 300 });
    const data = response?.data || {};
    processed += Number(data.processed || 0);
    written += Number(data.written || 0);
    pages += 1;
    onProgress?.({ processed, written, pages });
    cursor = data.nextCursor || null;
    if (!cursor) break;
  }

  return { processed, written, pages };
}

const getAssignedBusinessIds = (assignment) => {
  if (!assignment) return [];
  if (Array.isArray(assignment.businesses)) {
    return Array.from(
      new Set(
        assignment.businesses
          .map((entry) => String(entry?.businessId || "").trim())
          .filter(Boolean),
      ),
    );
  }
  if (assignment.businessId) {
    const singleId = String(assignment.businessId || "").trim();
    return singleId ? [singleId] : [];
  }
  return [];
};

const getAssignmentBusinesses = (assignment) => {
  if (!assignment) return [];
  if (Array.isArray(assignment?.businesses)) {
    return assignment.businesses
      .map((entry) => ({
        businessId: String(entry?.businessId || "").trim(),
        businessName: entry?.businessName || entry?.businessId || "",
        scope: entry?.scope || "all_locations",
        locations: Array.isArray(entry?.locations) ? entry.locations : [],
        status: entry?.status === "inactive" ? "inactive" : "active",
      }))
      .filter((entry) => entry.businessId);
  }
  if (assignment?.businessId) {
    const businessId = String(assignment.businessId || "").trim();
    if (!businessId) return [];
    return [
      {
        businessId,
        businessName: assignment.businessName || assignment.businessId,
        scope: assignment.scope || "all_locations",
        locations: Array.isArray(assignment.locations)
          ? assignment.locations
          : [],
        status: assignment?.status === "inactive" ? "inactive" : "active",
      },
    ];
  }
  return [];
};

const removeBusinessFromAssignment = (assignment, businessIdToRemove) => {
  const normalizedBusinessId = String(businessIdToRemove || "").trim();
  if (!normalizedBusinessId) return assignment || null;
  const remaining = getAssignmentBusinesses(assignment).filter(
    (entry) => String(entry?.businessId || "").trim() !== normalizedBusinessId,
  );
  if (remaining.length === 0) return null;
  return {
    mode: "multi_business",
    businessIds: remaining.map((entry) => entry.businessId),
    selectAllBusinesses: false,
    businesses: remaining,
  };
};

export const syncBusinessCollectionAssignments = async ({
  collectionId,
  collectionName = null,
  nextBusinessAssignment,
  previousBusinessAssignment = null,
  assignedByAdmin = false,
  forceActivateAssignedBusinesses = false,
  collectionIsDefaultFallback = false,
  collectionPublishStatus = "published",
}) => {
  const normalizedCollectionId = String(collectionId || "").trim();
  if (!normalizedCollectionId) throw new Error("collectionId is required.");

  const nextBusinessIds = getAssignedBusinessIds(nextBusinessAssignment);
  const previousBusinessIds = getAssignedBusinessIds(
    previousBusinessAssignment,
  );
  const toUpsert = nextBusinessIds;
  const toClear = previousBusinessIds.filter(
    (businessId) => !nextBusinessIds.includes(businessId),
  );

  const allIds = Array.from(new Set([...toUpsert, ...toClear]));
  if (allIds.length === 0) return { touched: 0 };

  const batch = writeBatch(businessDb);
  let touched = 0;

  const normalizedPublishStatus = String(
    collectionPublishStatus || "published",
  );
  const isCollectionPublished = normalizedPublishStatus === "published";

  for (const businessId of allIds) {
    const businessRef = doc(businessDb, "businesses", businessId);
    const snap = await getDoc(businessRef);
    if (!snap.exists()) continue;
    const data = snap.data() || {};
    const currentActive = String(data?.activeCollectionId || "").trim() || null;
    const currentPending =
      String(data?.pendingCollectionId || "").trim() || null;

    if (toClear.includes(businessId)) {
      const patch = {};
      if (currentActive === normalizedCollectionId) {
        const fallback = await resolveBusinessFallbackCollection(
          data,
          normalizedCollectionId,
        );
        if (fallback?.id) {
          patch.activeCollectionId = fallback.id;
          patch.activeCollectionName = fallback.name || null;
          patch.activeCollectionIsDefaultFallback = Boolean(
            fallback.isDefaultFallback,
          );
          patch.activeCollectionPublishStatus = String(
            fallback.publishStatus || "published",
          );
        } else {
          patch.activeCollectionId = null;
          patch.activeCollectionName = null;
          patch.activeCollectionIsDefaultFallback = null;
          patch.activeCollectionPublishStatus = null;
        }
      }
      if (currentPending === normalizedCollectionId) {
        patch.pendingCollectionId = null;
        patch.pendingCollectionName = null;
        patch.pendingCollectionIsDefaultFallback = null;
        patch.pendingCollectionPublishStatus = null;
        patch.pendingCollectionAssignedAt = null;
        patch.pendingCollectionAssignedBy = null;
      }
      if (Object.keys(patch).length > 0) {
        batch.set(businessRef, patch, { merge: true });
        touched += 1;
      }
      continue;
    }

    const patch = {};
    const activeIsFallback =
      data?.activeCollectionIsDefaultFallback === true
        ? true
        : currentActive
          ? await isCollectionDefaultFallback(currentActive)
          : false;

    // A draft/unpublished collection must never remain active on a business.
    if (!isCollectionPublished && currentActive === normalizedCollectionId) {
      const fallback = await resolveBusinessFallbackCollection(
        data,
        normalizedCollectionId,
      );
      if (fallback?.id) {
        patch.activeCollectionId = fallback.id;
        patch.activeCollectionName = fallback.name || null;
        patch.activeCollectionIsDefaultFallback = Boolean(
          fallback.isDefaultFallback,
        );
        patch.activeCollectionPublishStatus = String(
          fallback.publishStatus || "published",
        );
      } else {
        patch.activeCollectionId = null;
        patch.activeCollectionName = null;
        patch.activeCollectionIsDefaultFallback = null;
        patch.activeCollectionPublishStatus = null;
      }
    }

    if (assignedByAdmin) {
      if (!isCollectionPublished) {
        if (currentPending === normalizedCollectionId) {
          patch.pendingCollectionId = null;
          patch.pendingCollectionName = null;
          patch.pendingCollectionIsDefaultFallback = null;
          patch.pendingCollectionPublishStatus = null;
          patch.pendingCollectionAssignedAt = null;
          patch.pendingCollectionAssignedBy = null;
        }
      } else if (
        currentActive &&
        currentActive !== normalizedCollectionId &&
        !activeIsFallback
      ) {
        patch.pendingCollectionId = normalizedCollectionId;
        patch.pendingCollectionName = collectionName || null;
        patch.pendingCollectionIsDefaultFallback = Boolean(
          collectionIsDefaultFallback,
        );
        patch.pendingCollectionPublishStatus = normalizedPublishStatus;
        patch.pendingCollectionAssignedAt = serverTimestamp();
        patch.pendingCollectionAssignedBy = "admin";
      } else if (isCollectionPublished) {
        patch.activeCollectionId = normalizedCollectionId;
        patch.activeCollectionName = collectionName || null;
        patch.activeCollectionIsDefaultFallback = Boolean(
          collectionIsDefaultFallback,
        );
        patch.activeCollectionPublishStatus = normalizedPublishStatus;
        if (currentPending === normalizedCollectionId) {
          patch.pendingCollectionId = null;
          patch.pendingCollectionName = null;
          patch.pendingCollectionIsDefaultFallback = null;
          patch.pendingCollectionPublishStatus = null;
          patch.pendingCollectionAssignedAt = null;
          patch.pendingCollectionAssignedBy = null;
        }
      }
    } else {
      if (forceActivateAssignedBusinesses && isCollectionPublished) {
        patch.activeCollectionId = normalizedCollectionId;
        patch.activeCollectionName = collectionName || null;
        patch.activeCollectionIsDefaultFallback = Boolean(
          collectionIsDefaultFallback,
        );
        patch.activeCollectionPublishStatus = normalizedPublishStatus;
      }
      if (currentPending === normalizedCollectionId) {
        patch.pendingCollectionId = null;
        patch.pendingCollectionName = null;
        patch.pendingCollectionIsDefaultFallback = null;
        patch.pendingCollectionPublishStatus = null;
        patch.pendingCollectionAssignedAt = null;
        patch.pendingCollectionAssignedBy = null;
      }
    }

    if (Object.keys(patch).length > 0) {
      batch.set(businessRef, patch, { merge: true });
      touched += 1;
    }
  }

  if (touched > 0) {
    await batch.commit();
  }

  return { touched };
};

export const setBusinessActiveCollection = async (
  businessId,
  collectionId,
  collectionName = null,
) => {
  if (!businessId) throw new Error("businessId is required.");
  if (!collectionId) throw new Error("collectionId is required.");
  const businessRef = doc(businessDb, "businesses", businessId);
  const isDefaultFallback = await isCollectionDefaultFallback(collectionId);
  const collectionDoc = await getStudioCollectionById(collectionId);
  const publishStatus = String(collectionDoc?.publishStatus || "published");
  if (publishStatus !== "published") {
    throw new Error("Only published collections can be activated.");
  }
  const patch = {
    activeCollectionId: String(collectionId).trim(),
    activeCollectionName: collectionName || null,
    activeCollectionIsDefaultFallback: Boolean(isDefaultFallback),
    activeCollectionPublishStatus: publishStatus,
    updatedAt: serverTimestamp(),
  };
  const snap = await getDoc(businessRef);
  const current = snap.exists() ? snap.data() || {} : {};
  if (
    String(current?.pendingCollectionId || "").trim() ===
    String(collectionId).trim()
  ) {
    patch.pendingCollectionId = null;
    patch.pendingCollectionName = null;
    patch.pendingCollectionIsDefaultFallback = null;
    patch.pendingCollectionPublishStatus = null;
    patch.pendingCollectionAssignedAt = null;
    patch.pendingCollectionAssignedBy = null;
  }
  await updateDoc(businessRef, patch);
  const fresh = await getDoc(businessRef);
  return fresh.exists() ? { id: fresh.id, ...fresh.data() } : null;
};

export const acceptPendingCollectionAssignment = async (businessId) => {
  if (!businessId) throw new Error("businessId is required.");
  const businessRef = doc(businessDb, "businesses", businessId);
  const snap = await getDoc(businessRef);
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  const pendingCollectionId = String(data?.pendingCollectionId || "").trim();
  if (!pendingCollectionId) return { id: snap.id, ...data };
  const pendingCollectionDoc =
    await getStudioCollectionById(pendingCollectionId);
  if (
    String(pendingCollectionDoc?.publishStatus || "published") !== "published"
  ) {
    throw new Error("Only published collections can be activated.");
  }
  await updateDoc(businessRef, {
    activeCollectionId: pendingCollectionId,
    activeCollectionName: data?.pendingCollectionName || null,
    activeCollectionIsDefaultFallback: Boolean(
      data?.pendingCollectionIsDefaultFallback,
    ),
    activeCollectionPublishStatus: String(
      data?.pendingCollectionPublishStatus || "published",
    ),
    pendingCollectionId: null,
    pendingCollectionName: null,
    pendingCollectionIsDefaultFallback: null,
    pendingCollectionPublishStatus: null,
    pendingCollectionAssignedAt: null,
    pendingCollectionAssignedBy: null,
    updatedAt: serverTimestamp(),
  });
  const fresh = await getDoc(businessRef);
  return fresh.exists() ? { id: fresh.id, ...fresh.data() } : null;
};

export const rejectPendingCollectionAssignment = async (businessId) => {
  if (!businessId) throw new Error("businessId is required.");
  const businessRef = doc(businessDb, "businesses", businessId);
  await updateDoc(businessRef, {
    pendingCollectionId: null,
    pendingCollectionName: null,
    pendingCollectionIsDefaultFallback: null,
    pendingCollectionPublishStatus: null,
    pendingCollectionAssignedAt: null,
    pendingCollectionAssignedBy: null,
    updatedAt: serverTimestamp(),
  });
  const fresh = await getDoc(businessRef);
  return fresh.exists() ? { id: fresh.id, ...fresh.data() } : null;
};

export const reassignBusinessesWithUnavailableActiveCollection = async (
  collectionId,
  { preferredFallbackCollection = null } = {},
) => {
  const normalizedCollectionId = String(collectionId || "").trim();
  if (!normalizedCollectionId) return { touched: 0 };
  const preferredFallbackId = String(
    preferredFallbackCollection?.id || "",
  ).trim();
  const preferredFallback =
    preferredFallbackId && preferredFallbackId !== normalizedCollectionId
      ? {
          id: preferredFallbackId,
          name: preferredFallbackCollection?.name || null,
          isDefaultFallback: Boolean(
            preferredFallbackCollection?.isDefaultFallback,
          ),
          publishStatus: String(
            preferredFallbackCollection?.publishStatus || "published",
          ),
        }
      : null;

  const activeSnap = await getDocs(
    query(
      collection(businessDb, "businesses"),
      where("activeCollectionId", "==", normalizedCollectionId),
    ),
  );
  if (activeSnap.empty) return { touched: 0 };

  const batch = writeBatch(businessDb);
  let touched = 0;
  for (const docSnap of activeSnap.docs) {
    const business = { id: docSnap.id, ...(docSnap.data() || {}) };
    const fallback =
      preferredFallback ||
      (await resolveBusinessFallbackCollection(
        business,
        normalizedCollectionId,
      ));
    const patch = {};
    if (fallback?.id) {
      patch.activeCollectionId = fallback.id;
      patch.activeCollectionName = fallback.name || null;
      patch.activeCollectionIsDefaultFallback = Boolean(
        fallback.isDefaultFallback,
      );
      patch.activeCollectionPublishStatus = String(
        fallback.publishStatus || "published",
      );
    } else {
      patch.activeCollectionId = null;
      patch.activeCollectionName = null;
      patch.activeCollectionIsDefaultFallback = null;
      patch.activeCollectionPublishStatus = null;
    }
    if (
      String(business?.pendingCollectionId || "").trim() ===
      normalizedCollectionId
    ) {
      patch.pendingCollectionId = null;
      patch.pendingCollectionName = null;
      patch.pendingCollectionIsDefaultFallback = null;
      patch.pendingCollectionPublishStatus = null;
      patch.pendingCollectionAssignedAt = null;
      patch.pendingCollectionAssignedBy = null;
    }
    batch.set(doc(businessDb, "businesses", business.id), patch, {
      merge: true,
    });
    touched += 1;
  }

  if (touched > 0) {
    await batch.commit();
  }
  return { touched };
};

export const saveBusinessOnboarding = async ({
  uid,
  existingBusinessId = null,
  input,
  complete = false,
  isAdminActor = false,
}) => {
  if (!uid) throw new Error("UID is required.");
  const normalizedExistingBusinessId =
    String(existingBusinessId || "").trim() || null;
  if (!normalizedExistingBusinessId) {
    await assertOwnerAccountType({ uid, isAdminActor });
  } else {
    const actorRole = await assertCanEditBusiness({
      businessId: normalizedExistingBusinessId,
      actorUid: uid,
      isAdminActor,
    });
    if (actorRole === "owner") {
      await assertOwnerAccountType({ uid, isAdminActor });
    }
  }
  const normalized = normalizeBusinessInput(input);
  await assertUniqueBusinessName(normalized.companyName, {
    excludeBusinessId: normalizedExistingBusinessId || null,
  });
  const searchTokens = buildBusinessSearchTokens(normalized);
  const businessRef = normalizedExistingBusinessId
    ? doc(businessDb, "businesses", normalizedExistingBusinessId)
    : doc(collection(businessDb, "businesses"));
  const existingBusinessSnap = await getDoc(businessRef);
  const existingBusinessData = existingBusinessSnap.exists()
    ? existingBusinessSnap.data() || {}
    : null;
  const displayName = resolveBusinessDisplayName({
    id: businessRef.id,
    companyName: normalized.companyName,
  });

  const userRef = doc(db, "users", uid);
  const ownerUid =
    String(
      existingBusinessData?.ownerUid ||
        existingBusinessData?.createdByUid ||
        "",
    ).trim() || uid;
  const createdByUid =
    String(
      existingBusinessData?.createdByUid ||
        existingBusinessData?.ownerUid ||
        "",
    ).trim() || uid;
  const existingAdminUserIds = Array.isArray(existingBusinessData?.adminUserIds)
    ? existingBusinessData.adminUserIds
        .map((entry) => String(entry || "").trim())
        .filter(Boolean)
    : [];
  const adminUserIds =
    existingAdminUserIds.length > 0 ? existingAdminUserIds : [ownerUid];

  const businessBatch = writeBatch(businessDb);
  const now = serverTimestamp();
  const existingStatus = String(existingBusinessData?.status || "").trim();
  const status = complete
    ? BUSINESS_STATUS.ACTIVE
    : normalizedExistingBusinessId && existingStatus
      ? existingStatus
      : BUSINESS_STATUS.DRAFT;
  const currentActiveCollectionId = String(
    existingBusinessData?.activeCollectionId || "",
  ).trim();
  const fallbackCollection =
    complete && !currentActiveCollectionId
      ? await resolveBusinessFallbackCollection({
          ...(existingBusinessData || {}),
          id: businessRef.id,
          ownerUid: uid,
          createdByUid: uid,
        })
      : null;

  businessBatch.set(
    businessRef,
    {
      ...normalized,
      displayName,
      status,
      createdByUid,
      ownerUid,
      adminUserIds,
      // memberUids drives member-scoped Firestore reads. On create we must write
      // a concrete array (the security rule validates the owner is included, and
      // FieldValue transforms are not resolved at rule-evaluation time). On
      // update we union so we never clobber the trigger-maintained roster.
      memberUids: normalizedExistingBusinessId
        ? arrayUnion(ownerUid)
        : [ownerUid],
      searchTokens,
      updatedByUid: auditActorUid(uid),
      updatedAt: now,
      ...(fallbackCollection?.id
        ? {
            activeCollectionId: fallbackCollection.id,
            activeCollectionName: fallbackCollection.name || null,
            activeCollectionIsDefaultFallback: Boolean(
              fallbackCollection.isDefaultFallback,
            ),
            activeCollectionPublishStatus: String(
              fallbackCollection.publishStatus || "published",
            ),
          }
        : {}),
      ...(normalizedExistingBusinessId ? {} : { createdAt: now }),
    },
    { merge: true },
  );

  if (!normalizedExistingBusinessId) {
    const membershipRef = doc(
      businessDb,
      "businessMemberships",
      `${businessRef.id}_${uid}`,
    );
    businessBatch.set(
      membershipRef,
      {
        businessId: businessRef.id,
        uid,
        role: "owner",
        status: "active",
        updatedAt: now,
        createdAt: now,
      },
      { merge: true },
    );
  }

  const userBatch = writeBatch(db);
  userBatch.set(
    userRef,
    {
      primaryBusinessId: businessRef.id,
      onboardingStatus: complete
        ? ONBOARDING_STATUS.COMPLETED
        : ONBOARDING_STATUS.IN_PROGRESS,
      updatedAt: now,
    },
    { merge: true },
  );

  let businessCommitDone = false;
  try {
    await businessBatch.commit();
    businessCommitDone = true;
    await userBatch.commit();
  } catch (error) {
    console.error(
      `[saveBusinessOnboarding] ${
        businessCommitDone
          ? "userBatch (users / default DB)"
          : "businessBatch (businesses + membership / studio DB)"
      } commit failed:`,
      error,
    );
    throw error;
  }

  if (normalizedExistingBusinessId) {
    // Best-effort side-effect: syncing a business's website onto its time
    // capsules' call-to-action links must never block saving the profile
    // itself. Time capsules live in the default DB under capsule-specific
    // rules, so a business owner may not have write access to them.
    try {
      await updateTimeCapsuleBusinessWebsiteLinks({
        existing: { id: businessRef.id, ...(existingBusinessData || {}) },
        saved: { id: businessRef.id, websiteUrl: normalized.websiteUrl },
      });
    } catch (error) {
      console.warn(
        "[saveBusinessOnboarding] Skipped time capsule website link sync:",
        error?.message || error,
      );
    }
  }

  return {
    businessId: businessRef.id,
    onboardingStatus: complete
      ? ONBOARDING_STATUS.COMPLETED
      : ONBOARDING_STATUS.IN_PROGRESS,
  };
};

export const updateBusinessProfile = async (
  businessId,
  profilePatch = {},
  { actorUid = null, isAdminActor = false } = {},
) => {
  if (!businessId) throw new Error("businessId is required.");
  const normalizedActorUid = String(actorUid || "").trim();
  if (!normalizedActorUid && !isAdminActor) {
    throw new Error("actorUid is required.");
  }
  const actorRole = await assertCanEditBusiness({
    businessId,
    actorUid: normalizedActorUid,
    isAdminActor,
  });
  if (actorRole === "owner") {
    await assertOwnerAccountType({ uid: normalizedActorUid, isAdminActor });
  }
  const normalized = normalizeBusinessInput(profilePatch);
  await assertUniqueBusinessName(normalized.companyName, {
    excludeBusinessId: businessId,
  });
  const displayName = resolveBusinessDisplayName({
    id: businessId,
    companyName: normalized.companyName,
    displayName: profilePatch?.displayName,
  });
  const businessRef = doc(businessDb, "businesses", businessId);
  const existingSnap = await getDoc(businessRef);
  const existingBusiness = existingSnap.exists()
    ? { id: existingSnap.id, ...(existingSnap.data() || {}) }
    : null;
  await updateDoc(businessRef, {
    ...normalized,
    displayName,
    searchTokens: buildBusinessSearchTokens(normalized),
    updatedByUid: auditActorUid(normalizedActorUid),
    updatedAt: serverTimestamp(),
  });
  const snap = await getDoc(businessRef);
  const savedBusiness = snap.exists() ? { id: snap.id, ...snap.data() } : null;
  await updateTimeCapsuleBusinessWebsiteLinks({
    existing: existingBusiness,
    saved: savedBusiness,
  });
  return savedBusiness;
};

const getBusinessActivationChecks = (business = {}) => {
  const companyName = String(business?.companyName || "").trim();
  const hqAddress = String(business?.hqAddress || "").trim();
  const businessCategories = Array.isArray(business?.businessCategories)
    ? business.businessCategories
    : [];
  const approxEmployeeCount = String(
    business?.approxEmployeeCount || "",
  ).trim();
  const avatarUrl = String(business?.avatarUrl || "").trim();
  const pinUrl = String(business?.pinUrl || "").trim();
  const adRestrictionsEnabled =
    business?.adRestrictions?.enabled === undefined
      ? false
      : Boolean(business?.adRestrictions?.enabled);
  const adRestrictionCategories = Array.isArray(
    business?.adRestrictions?.categories,
  )
    ? business.adRestrictions.categories
    : [];
  const adBlockedKeywords = Array.isArray(
    business?.adRestrictions?.blockedKeywords,
  )
    ? business.adRestrictions.blockedKeywords
    : [];

  return [
    {
      key: "company_name",
      label: "Company name added",
      complete: Boolean(companyName),
    },
    {
      key: "hq_address",
      label: "HQ address added",
      complete: Boolean(hqAddress),
    },
    {
      key: "categories",
      label: "At least one business category selected",
      complete: businessCategories.length > 0,
    },
    {
      key: "employee_count",
      label: "Approximate employee count selected",
      complete: Boolean(approxEmployeeCount),
    },
    {
      key: "business_logo",
      label: "Business logo & map pin added",
      complete: Boolean(avatarUrl) && Boolean(pinUrl),
    },
    {
      key: "ad_restrictions",
      label: "Ad restrictions configured",
      complete:
        !adRestrictionsEnabled ||
        adRestrictionCategories.length > 0 ||
        adBlockedKeywords.length > 0,
    },
  ];
};

export const activateBusinessProfile = async ({
  businessId,
  uid,
  isAdminActor = false,
}) => {
  if (!businessId) throw new Error("businessId is required.");
  if (!uid) throw new Error("uid is required.");
  const actorRole = await assertCanEditBusiness({
    businessId,
    actorUid: uid,
    isAdminActor,
  });
  if (actorRole === "owner") {
    await assertOwnerAccountType({ uid, isAdminActor });
  }

  const normalizedBusinessId = String(businessId || "").trim();
  const businessRef = doc(businessDb, "businesses", normalizedBusinessId);
  const businessSnap = await getDoc(businessRef);
  if (!businessSnap.exists()) throw new Error("Business not found.");
  const business = { id: businessSnap.id, ...(businessSnap.data() || {}) };

  const checks = getBusinessActivationChecks(business);
  const missing = checks.filter((item) => !item.complete);
  if (missing.length > 0) {
    throw new Error(
      `Business is incomplete: ${missing.map((item) => item.label).join(", ")}.`,
    );
  }

  const fallback = await resolveBusinessFallbackCollection(business);
  const activeCollectionId = String(business?.activeCollectionId || "").trim();
  const nextActiveCollectionId =
    activeCollectionId || String(fallback?.id || "").trim() || null;

  const patch = {
    status: BUSINESS_STATUS.ACTIVE,
    updatedByUid: auditActorUid(uid),
    updatedAt: serverTimestamp(),
  };
  if (nextActiveCollectionId) {
    patch.activeCollectionId = nextActiveCollectionId;
    patch.activeCollectionName =
      fallback?.name || business?.activeCollectionName || null;
    patch.activeCollectionIsDefaultFallback = Boolean(
      fallback?.isDefaultFallback ??
      business?.activeCollectionIsDefaultFallback,
    );
    patch.activeCollectionPublishStatus = String(
      fallback?.publishStatus ||
        business?.activeCollectionPublishStatus ||
        "published",
    );
  }

  await setDoc(businessRef, patch, { merge: true });
  if (actorRole === "owner" && !isAdminActor) {
    await setDoc(
      doc(db, "users", uid),
      {
        accountType: ACCOUNT_TYPES.BUSINESS,
        onboardingStatus: ONBOARDING_STATUS.COMPLETED,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }

  const refreshed = await getDoc(businessRef);
  return refreshed.exists()
    ? { id: refreshed.id, ...(refreshed.data() || {}) }
    : null;
};

export const deactivateBusinessProfile = async ({
  businessId,
  uid,
  isAdminActor = false,
}) => {
  if (!businessId) throw new Error("businessId is required.");
  if (!uid) throw new Error("uid is required.");
  await assertCanEditBusiness({
    businessId,
    actorUid: uid,
    isAdminActor,
  });

  const normalizedBusinessId = String(businessId || "").trim();
  const businessRef = doc(businessDb, "businesses", normalizedBusinessId);
  const businessSnap = await getDoc(businessRef);
  if (!businessSnap.exists()) throw new Error("Business not found.");

  await updateDoc(businessRef, {
    status: BUSINESS_STATUS.DRAFT,
    updatedByUid: auditActorUid(uid),
    updatedAt: serverTimestamp(),
  });

  const refreshed = await getDoc(businessRef);
  return refreshed.exists()
    ? { id: refreshed.id, ...(refreshed.data() || {}) }
    : null;
};

export const uploadBusinessAvatar = async ({
  businessId,
  uid,
  blob,
  onProgress,
} = {}) => {
  if (!businessId) throw new Error("businessId is required.");
  if (!uid) throw new Error("uid is required.");
  if (!(blob instanceof Blob))
    throw new Error("A valid avatar blob is required.");

  const businessRef = doc(
    businessDb,
    "businesses",
    String(businessId || "").trim(),
  );
  const businessSnap = await getDoc(businessRef);
  if (!businessSnap.exists()) throw new Error("Business not found.");
  const previousStoragePath = String(
    businessSnap.data()?.avatarStoragePath || "",
  ).trim();

  const storagePath = `businesses/${businessId}/avatar.webp`;
  const fileRef = ref(storage, storagePath);

  await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(fileRef, blob, {
      contentType: "image/webp",
      cacheControl: "public,max-age=31536000,immutable",
    });
    task.on(
      "state_changed",
      (snapshot) => {
        const total = Number(snapshot?.totalBytes || 0);
        const loaded = Number(snapshot?.bytesTransferred || 0);
        const progress = total > 0 ? Math.round((loaded / total) * 100) : 0;
        onProgress?.(progress);
      },
      (error) => reject(error),
      () => resolve(true),
    );
  });

  const avatarUrl = await getDownloadURL(fileRef);
  await updateDoc(doc(businessDb, "businesses", businessId), {
    avatarUrl,
    avatarStoragePath: storagePath,
    updatedByUid: auditActorUid(uid),
    updatedAt: serverTimestamp(),
  });
  if (previousStoragePath && previousStoragePath !== storagePath) {
    await safeDeleteStoragePath(previousStoragePath);
  }
  return {
    avatarUrl,
    avatarStoragePath: storagePath,
  };
};

export const uploadBusinessPin = async ({
  businessId,
  uid,
  blob,
  onProgress,
  templateName = "",
  templateUpdatedAtMs = 0,
} = {}) => {
  if (!businessId) throw new Error("businessId is required.");
  if (!uid) throw new Error("uid is required.");
  if (!(blob instanceof Blob)) throw new Error("A valid pin blob is required.");

  const businessRef = doc(
    businessDb,
    "businesses",
    String(businessId || "").trim(),
  );
  const businessSnap = await getDoc(businessRef);
  if (!businessSnap.exists()) throw new Error("Business not found.");
  const previousStoragePath = String(
    businessSnap.data()?.pinStoragePath || "",
  ).trim();

  const storagePath = `businesses/${businessId}/pin.webp`;
  const fileRef = ref(storage, storagePath);

  await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(fileRef, blob, {
      contentType: "image/webp",
      cacheControl: "public,max-age=31536000,immutable",
    });
    task.on(
      "state_changed",
      (snapshot) => {
        const total = Number(snapshot?.totalBytes || 0);
        const loaded = Number(snapshot?.bytesTransferred || 0);
        const progress = total > 0 ? Math.round((loaded / total) * 100) : 0;
        onProgress?.(progress);
      },
      (error) => reject(error),
      () => resolve(true),
    );
  });

  const pinUrl = await getDownloadURL(fileRef);
  const templatePatch = String(templateName || "").trim()
    ? {
        pinTemplateName: String(templateName || "").trim(),
        pinTemplateUpdatedAtMs: Number(templateUpdatedAtMs) || 0,
        pinGeneratedAtMs: Date.now(),
      }
    : {};
  await updateDoc(doc(businessDb, "businesses", businessId), {
    pinUrl,
    pinStoragePath: storagePath,
    ...templatePatch,
    updatedByUid: auditActorUid(uid),
    updatedAt: serverTimestamp(),
  });
  if (previousStoragePath && previousStoragePath !== storagePath) {
    await safeDeleteStoragePath(previousStoragePath);
  }
  return {
    pinUrl,
    pinStoragePath: storagePath,
  };
};

export const listBusinessMemberships = async (businessId) => {
  if (!businessId) return [];
  const q = query(
    collection(businessDb, "businessMemberships"),
    where("businessId", "==", businessId),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const listBusinessMembershipsForUser = async (uid) => {
  const normalizedUid = String(uid || "").trim();
  if (!normalizedUid) return [];
  const membershipsQuery = query(
    collection(businessDb, "businessMemberships"),
    where("uid", "==", normalizedUid),
  );
  const snapshot = await getDocs(membershipsQuery);
  return snapshot.docs
    .map((d) => ({ id: d.id, ...(d.data() || {}) }))
    .filter((row) => String(row?.status || "active") !== "inactive");
};

export const getBusinessMembershipForUser = async ({ businessId, uid }) => {
  const normalizedBusinessId = String(businessId || "").trim();
  const normalizedUid = String(uid || "").trim();
  if (!normalizedBusinessId || !normalizedUid) return null;

  const directRef = doc(
    businessDb,
    "businessMemberships",
    `${normalizedBusinessId}_${normalizedUid}`,
  );
  const directSnap = await getDoc(directRef);
  if (directSnap.exists()) {
    const row = { id: directSnap.id, ...(directSnap.data() || {}) };
    return String(row?.status || "active") === "inactive" ? null : row;
  }

  const fallbackQuery = query(
    collection(businessDb, "businessMemberships"),
    where("businessId", "==", normalizedBusinessId),
    where("uid", "==", normalizedUid),
    limit(1),
  );
  const fallbackSnap = await getDocs(fallbackQuery);
  if (fallbackSnap.empty) return null;
  const row = {
    id: fallbackSnap.docs[0].id,
    ...(fallbackSnap.docs[0].data() || {}),
  };
  return String(row?.status || "active") === "inactive" ? null : row;
};

export const subscribeBusinessMembershipForUser = (
  { businessId, uid },
  onNext,
  onError,
) => {
  const normalizedBusinessId = String(businessId || "").trim();
  const normalizedUid = String(uid || "").trim();
  if (!normalizedBusinessId || !normalizedUid) {
    onNext?.(null);
    return () => {};
  }

  return onSnapshot(
    query(
      collection(businessDb, "businessMemberships"),
      where("businessId", "==", normalizedBusinessId),
      where("uid", "==", normalizedUid),
      limit(1),
    ),
    (snapshot) => {
      if (snapshot.empty) {
        onNext?.(null);
        return;
      }
      const docSnap = snapshot.docs[0];
      const row = { id: docSnap.id, ...(docSnap.data() || {}) };
      onNext?.(String(row?.status || "active") === "inactive" ? null : row);
    },
    (error) => {
      onError?.(error);
    },
  );
};

export const leaveBusinessAsEmployee = async ({ businessId, uid }) => {
  const normalizedBusinessId = String(businessId || "").trim();
  const normalizedUid = String(uid || "").trim();
  if (!normalizedBusinessId) throw new Error("businessId is required.");
  if (!normalizedUid) throw new Error("uid is required.");

  const membership = await getBusinessMembershipForUser({
    businessId: normalizedBusinessId,
    uid: normalizedUid,
  });
  if (!membership?.id) throw new Error("Membership not found.");

  const role = String(membership?.role || "")
    .trim()
    .toLowerCase();
  if (role === "owner") {
    throw new Error(
      "Owners cannot leave the business. Transfer ownership or delete the business instead.",
    );
  }

  // memberUids is reconciled server-side by the syncBusinessMemberUids trigger;
  // a departing employee is not privileged to write the business doc.
  await updateDoc(doc(businessDb, "businessMemberships", membership.id), {
    status: "inactive",
    leftAt: serverTimestamp(),
    leftByUid: normalizedUid,
    leftReason: "left",
    updatedAt: serverTimestamp(),
  });

  const remainingMemberships =
    await listBusinessMembershipsForUser(normalizedUid);
  const ownerMembership = remainingMemberships.find(
    (row) =>
      String(row?.role || "")
        .trim()
        .toLowerCase() === "owner",
  );
  const nextPrimaryBusinessId =
    String(ownerMembership?.businessId || "").trim() ||
    String(remainingMemberships[0]?.businessId || "").trim() ||
    null;

  await setDoc(
    doc(db, "users", normalizedUid),
    {
      primaryBusinessId: nextPrimaryBusinessId,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  return { ok: true, nextPrimaryBusinessId };
};

export const listBusinessInvites = async (businessId) => {
  if (!businessId) return [];
  const q = query(
    collection(businessDb, "businessInvites"),
    where("businessId", "==", businessId),
    where("status", "==", "pending"),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const subscribeBusinessMemberships = (businessId, onNext, onError) => {
  const normalizedBusinessId = String(businessId || "").trim();
  if (!normalizedBusinessId) {
    onNext?.([]);
    return () => {};
  }
  return onSnapshot(
    query(
      collection(businessDb, "businessMemberships"),
      where("businessId", "==", normalizedBusinessId),
    ),
    (snapshot) => {
      onNext?.(snapshot.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
    },
    (error) => {
      onError?.(error);
    },
  );
};

export const subscribeBusinessInvites = (businessId, onNext, onError) => {
  const normalizedBusinessId = String(businessId || "").trim();
  if (!normalizedBusinessId) {
    onNext?.([]);
    return () => {};
  }
  return onSnapshot(
    query(
      collection(businessDb, "businessInvites"),
      where("businessId", "==", normalizedBusinessId),
    ),
    (snapshot) => {
      onNext?.(snapshot.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
    },
    (error) => {
      onError?.(error);
    },
  );
};

export const listPendingBusinessInvitesForEmail = async (email) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) return [];
  const invitesQuery = query(
    collection(businessDb, "businessInvites"),
    where("email", "==", normalizedEmail),
    where("status", "==", "pending"),
  );
  const invitesSnap = await getDocs(invitesQuery);
  if (invitesSnap.empty) return [];

  const invites = invitesSnap.docs
    .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }))
    .sort(
      (a, b) =>
        toMillis(b.updatedAt || b.createdAt) -
        toMillis(a.updatedAt || a.createdAt),
    );
  const businessIds = Array.from(
    new Set(
      invites
        .map((invite) => String(invite?.businessId || "").trim())
        .filter(Boolean),
    ),
  );

  const businessById = {};
  const chunkSize = 10;
  for (let i = 0; i < businessIds.length; i += chunkSize) {
    const slice = businessIds.slice(i, i + chunkSize);
    const businessesSnap = await getDocs(
      query(
        collection(businessDb, "businesses"),
        where(documentId(), "in", slice),
      ),
    );
    businessesSnap.forEach((businessDoc) => {
      businessById[businessDoc.id] = {
        id: businessDoc.id,
        ...(businessDoc.data() || {}),
      };
    });
  }

  return invites.map((invite) => {
    const businessId = String(invite?.businessId || "").trim();
    const business = businessById[businessId] || null;
    return {
      ...invite,
      business,
      businessName: resolveBusinessDisplayName({
        id: businessId,
        companyName: business?.companyName,
        displayName: business?.displayName,
      }),
    };
  });
};

export const subscribePendingBusinessInvitesForEmail = (
  email,
  onNext,
  onError,
) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    onNext?.([]);
    return () => {};
  }

  const invitesQuery = query(
    collection(businessDb, "businessInvites"),
    where("email", "==", normalizedEmail),
    where("status", "==", "pending"),
  );

  return onSnapshot(
    invitesQuery,
    async (invitesSnap) => {
      const invites = invitesSnap.docs
        .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }))
        .sort(
          (a, b) =>
            toMillis(b.updatedAt || b.createdAt) -
            toMillis(a.updatedAt || a.createdAt),
        );

      const businessIds = Array.from(
        new Set(
          invites
            .map((invite) => String(invite?.businessId || "").trim())
            .filter(Boolean),
        ),
      );
      const businessById = {};
      const chunkSize = 10;
      for (let i = 0; i < businessIds.length; i += chunkSize) {
        const slice = businessIds.slice(i, i + chunkSize);
        const businessesSnap = await getDocs(
          query(
            collection(businessDb, "businesses"),
            where(documentId(), "in", slice),
          ),
        );
        businessesSnap.forEach((businessDoc) => {
          businessById[businessDoc.id] = {
            id: businessDoc.id,
            ...(businessDoc.data() || {}),
          };
        });
      }

      onNext?.(
        invites.map((invite) => {
          const businessId = String(invite?.businessId || "").trim();
          const business = businessById[businessId] || null;
          return {
            ...invite,
            business,
            businessName: resolveBusinessDisplayName({
              id: businessId,
              companyName: business?.companyName,
              displayName: business?.displayName,
            }),
          };
        }),
      );
    },
    (error) => onError?.(error),
  );
};

export const createBusinessInvite = async ({
  businessId,
  invitedByUid,
  isAdminInviter = false,
  email,
  role,
  scopes,
}) => {
  if (!businessId) throw new Error("businessId is required.");
  if (!email) throw new Error("email is required.");
  if (!invitedByUid && !isAdminInviter)
    throw new Error("invitedByUid is required.");
  const targetRole = normalizeBusinessMembershipRole(role, { fallback: "" });
  if (!targetRole || targetRole === "owner") {
    throw new Error("Invites can only assign manager or collaborator roles.");
  }
  await assertCanManageTeamTarget({
    businessId,
    actorUid: invitedByUid,
    isAdminActor: isAdminInviter,
    targetRole,
  });

  const payload = {
    businessId,
    invitedByUid,
    email: normalizeEmail(email),
    role: targetRole,
    scopes: normalizeEmployeeScopes(scopes, { requireOne: true }),
    status: "pending",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(businessDb, "businessInvites"), payload);
  const refreshedSnap = await getDoc(ref);
  return refreshedSnap.exists()
    ? { id: refreshedSnap.id, ...refreshedSnap.data() }
    : null;
};

export const respondToBusinessInvite = async ({
  inviteId,
  uid,
  action,
  switchToBusinessOnAccept = false,
  isAdminResponder = false,
}) => {
  const normalizedInviteId = String(inviteId || "").trim();
  const normalizedUid = String(uid || "").trim();
  const normalizedAction = String(action || "")
    .trim()
    .toLowerCase();
  const isAccept = normalizedAction === "accept";
  const isDecline = normalizedAction === "decline";

  if (!normalizedInviteId) throw new Error("inviteId is required.");
  if (!normalizedUid) throw new Error("uid is required.");
  if (!isAccept && !isDecline)
    throw new Error("action must be either accept or decline.");

  const inviteRef = doc(businessDb, "businessInvites", normalizedInviteId);
  const inviteSnap = await getDoc(inviteRef);
  if (!inviteSnap.exists()) throw new Error("Invite not found.");
  const invite = { id: inviteSnap.id, ...(inviteSnap.data() || {}) };
  if (String(invite?.status || "").toLowerCase() !== "pending") {
    throw new Error("This invite is no longer pending.");
  }

  const businessId = String(invite?.businessId || "").trim();
  if (!businessId) throw new Error("Invite is missing a businessId.");

  const batch = writeBatch(businessDb);
  if (isAccept) {
    const acceptedRole = normalizeBusinessMembershipRole(invite?.role, {
      fallback: "",
    });
    if (!acceptedRole) {
      throw new Error("Invite has an invalid role.");
    }
    if (acceptedRole === "owner") {
      await assertOwnerAccountType({
        uid: normalizedUid,
        isAdminActor: Boolean(isAdminResponder),
      });
    }
    const membershipRef = doc(
      businessDb,
      "businessMemberships",
      `${businessId}_${normalizedUid}`,
    );
    batch.set(
      membershipRef,
      {
        businessId,
        uid: normalizedUid,
        role: acceptedRole,
        scopes: normalizeEmployeeScopes(invite?.scopes, { requireOne: false }),
        isAdminAssignee: Boolean(isAdminResponder),
        status: "active",
        invitedByUid: invite?.invitedByUid || null,
        inviteId: invite.id,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      },
      { merge: true },
    );
    // The business memberUids roster is maintained server-side by the
    // syncBusinessMemberUids trigger on businessMemberships writes; the invitee
    // is not yet privileged on this business, so it cannot write it here.
  }
  batch.set(
    inviteRef,
    {
      status: isAccept ? "accepted" : "declined",
      respondedByUid: normalizedUid,
      respondedAt: serverTimestamp(),
      acceptedAt: isAccept ? serverTimestamp() : null,
      declinedAt: isDecline ? serverTimestamp() : null,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
  await batch.commit();

  // Reported back so the caller can mirror it into the local profile store
  // without a refetch — otherwise the UI still holds a typeless profile and
  // sends the user to the account-type chooser on the next navigation.
  let appliedAccountType = null;

  if (isAccept && !isAdminResponder) {
    const profilePatch = {};

    if (switchToBusinessOnAccept) {
      profilePatch.primaryBusinessId = businessId;
    }

    // Someone who signed up from an invite email has no account type yet:
    // getPostAuthDestination sends them straight to the invitations page and
    // skips the chooser, so nothing has asked them. Being invited into another
    // company's business does not make them a business account — they are an
    // individual collaborating there, which is exactly the case
    // useActiveBusinessMembership already grants business access for. Typing
    // them here is what stops the chooser reappearing on the next navigation.
    //
    // Only ever fills a MISSING type. Never re-types an existing account, which
    // would silently downgrade a business owner who accepted an invite.
    const profile = await getUserProfileRow(normalizedUid);
    const existingAccountType = String(profile?.accountType || "")
      .trim()
      .toLowerCase();
    if (!Object.values(ACCOUNT_TYPES).includes(existingAccountType)) {
      profilePatch.accountType = ACCOUNT_TYPES.INDIVIDUAL;
      profilePatch.onboardingStatus = ONBOARDING_STATUS.COMPLETED;
      appliedAccountType = ACCOUNT_TYPES.INDIVIDUAL;
    }

    // One merged patch on purpose. setUserAccountType nulls primaryBusinessId
    // when it writes INDIVIDUAL, so typing them in a second write would undo
    // the switch we just made.
    if (Object.keys(profilePatch).length > 0) {
      await setDoc(
        doc(db, "users", normalizedUid),
        {
          ...profilePatch,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    }
  }

  return {
    ok: true,
    status: isAccept ? "accepted" : "declined",
    businessId,
    switchedAccountContext: Boolean(
      isAccept && switchToBusinessOnAccept && !isAdminResponder,
    ),
    isAdminAssignee: Boolean(isAccept && isAdminResponder),
    appliedAccountType,
  };
};

export const updateBusinessMembership = async (
  membershipId,
  patch = {},
  { actorUid = null, isAdminActor = false } = {},
) => {
  if (!membershipId) throw new Error("membershipId is required.");
  const ref = doc(businessDb, "businessMemberships", membershipId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Membership not found.");
  const currentMembership = { id: snap.id, ...(snap.data() || {}) };
  const businessId = String(currentMembership?.businessId || "").trim();
  if (!businessId) throw new Error("Membership is missing businessId.");
  const nextRole =
    patch.role !== undefined
      ? normalizeBusinessMembershipRole(patch.role, { fallback: "" })
      : normalizeBusinessMembershipRole(currentMembership?.role, {
          fallback: "",
        });
  await assertCanManageTeamTarget({
    businessId,
    actorUid,
    isAdminActor,
    targetRole: nextRole,
  });

  const normalizedPatch = {
    ...patch,
  };
  if (normalizedPatch.role !== undefined) {
    normalizedPatch.role = normalizeBusinessMembershipRole(
      normalizedPatch.role,
      { fallback: "" },
    );
    if (!normalizedPatch.role) {
      throw new Error("Invalid team role.");
    }
  }
  if (normalizedPatch.scopes !== undefined) {
    normalizedPatch.scopes = normalizeEmployeeScopes(normalizedPatch.scopes, {
      requireOne: true,
    });
  }
  // memberUids is reconciled server-side by the syncBusinessMemberUids trigger
  // whenever a membership's status changes.
  await updateDoc(ref, {
    ...normalizedPatch,
    updatedByUid: auditActorUid(actorUid),
    updatedAt: serverTimestamp(),
  });
  const refreshedSnap = await getDoc(ref);
  return refreshedSnap.exists()
    ? { id: refreshedSnap.id, ...refreshedSnap.data() }
    : null;
};

export const removeBusinessMembership = async (
  membershipId,
  { actorUid = null, isAdminActor = false } = {},
) => {
  if (!membershipId) throw new Error("membershipId is required.");
  const membershipRef = doc(businessDb, "businessMemberships", membershipId);
  const snap = await getDoc(membershipRef);
  if (!snap.exists()) throw new Error("Membership not found.");
  const membership = { id: snap.id, ...(snap.data() || {}) };
  const businessId = String(membership?.businessId || "").trim();
  const targetRole = normalizeBusinessMembershipRole(membership?.role, {
    fallback: "",
  });
  await assertCanManageTeamTarget({
    businessId,
    actorUid,
    isAdminActor,
    targetRole,
  });
  if (targetRole === "owner") {
    throw new Error("Owner membership cannot be removed.");
  }
  // memberUids is reconciled server-side by the syncBusinessMemberUids trigger.
  await deleteDoc(membershipRef);
  return { ok: true };
};

export const cancelBusinessInvite = async (
  inviteId,
  { actorUid = null, isAdminActor = false } = {},
) => {
  if (!inviteId) throw new Error("inviteId is required.");
  const inviteRef = doc(businessDb, "businessInvites", inviteId);
  const inviteSnap = await getDoc(inviteRef);
  if (!inviteSnap.exists()) throw new Error("Invite not found.");
  const invite = { id: inviteSnap.id, ...(inviteSnap.data() || {}) };
  await assertCanManageTeamTarget({
    businessId: invite?.businessId,
    actorUid,
    isAdminActor,
    targetRole: invite?.role,
  });
  await deleteDoc(inviteRef);
  return { ok: true };
};

export const softDeleteBusiness = async ({
  businessId,
  uid,
  nextPrimaryBusinessId = null,
  isAdminActor = false,
}) => {
  if (!businessId) throw new Error("businessId is required.");
  if (!uid) throw new Error("uid is required.");
  const actorRole = await assertCanDeleteBusiness({
    businessId,
    actorUid: uid,
    isAdminActor,
  });
  if (actorRole === "owner") {
    await assertOwnerAccountType({ uid, isAdminActor });
  }
  const normalizedBusinessId = String(businessId || "").trim();
  const normalizedUid = String(uid || "").trim();

  const businessSnap = await getDoc(
    doc(businessDb, "businesses", normalizedBusinessId),
  );
  const business = businessSnap.exists()
    ? { id: businessSnap.id, ...(businessSnap.data() || {}) }
    : null;

  const businessOwnerUid = String(
    business?.ownerUid || business?.createdByUid || "",
  ).trim();
  const ownerUid = businessOwnerUid || String(uid || "").trim();
  let ownedCollections = [];
  try {
    const ownedCollectionsSnap = await getDocs(
      query(
        collection(studioDb, "collections"),
        where("createdBy", "==", ownerUid),
      ),
    );
    ownedCollections = ownedCollectionsSnap.docs.map((d) => ({
      id: d.id,
      ...(d.data() || {}),
    }));
  } catch (error) {
    if (!isPermissionDeniedError(error)) throw error;
    console.warn("Skipping owned collection lookup due to permissions.");
  }
  const ownedCollectionIds = new Set(ownedCollections.map((row) => row.id));

  let assignedCollections = [];
  try {
    const assignedCollectionsSnap = await getDocs(
      query(
        collection(studioDb, "collections"),
        where(
          "businessAssignment.businessIds",
          "array-contains",
          normalizedBusinessId,
        ),
      ),
    );
    assignedCollections = assignedCollectionsSnap.docs.map((d) => ({
      id: d.id,
      ...(d.data() || {}),
    }));
  } catch (error) {
    if (!isPermissionDeniedError(error)) throw error;
    console.warn("Skipping assigned collection lookup due to permissions.");
  }
  const adminAssignedCollections = assignedCollections.filter(
    (row) => !ownedCollectionIds.has(row.id),
  );

  for (const row of adminAssignedCollections) {
    const nextAssignment = removeBusinessFromAssignment(
      row?.businessAssignment,
      normalizedBusinessId,
    );
    try {
      await updateDoc(doc(studioDb, "collections", row.id), {
        businessAssignment: nextAssignment,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      if (!isPermissionDeniedError(error)) throw error;
      console.warn(
        "Skipping assigned collection cleanup due to permissions:",
        row.id,
      );
    }
  }

  for (const row of ownedCollections) {
    const collectionId = String(row?.id || "").trim();
    if (!collectionId) continue;

    if (row?.businessAssignment != null) {
      try {
        await syncBusinessCollectionAssignments({
          collectionId,
          previousBusinessAssignment: row.businessAssignment,
          nextBusinessAssignment: null,
          assignedByAdmin: false,
          collectionIsDefaultFallback: Boolean(row?.isDefaultFallback),
          collectionPublishStatus: String(row?.publishStatus || "published"),
        });
        await updateDoc(doc(studioDb, "collections", collectionId), {
          businessAssignment: null,
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        if (!isPermissionDeniedError(error)) throw error;
        console.warn(
          "Skipping owned collection assignment cleanup due to permissions:",
          collectionId,
        );
      }
    }

    try {
      await reassignBusinessesWithUnavailableActiveCollection(collectionId);
    } catch (error) {
      if (!isPermissionDeniedError(error)) throw error;
      console.warn(
        "Skipping business active-collection reassign due to permissions:",
        collectionId,
      );
    }

    try {
      const usedRowsSnap = await getDocs(
        query(
          collection(studioDb, "tvRows"),
          where("sourceType", "==", "collection"),
          where("sourceRef", "==", collectionId),
        ),
      );
      if (!usedRowsSnap.empty) {
        const tvRowsBatch = writeBatch(studioDb);
        usedRowsSnap.docs.forEach((snap) => {
          tvRowsBatch.delete(snap.ref);
        });
        await tvRowsBatch.commit();
      }
    } catch (error) {
      if (!isPermissionDeniedError(error)) throw error;
      console.warn("Skipping tvRows cleanup due to permissions:", collectionId);
    }

    try {
      await deleteDoc(doc(studioDb, "collections", collectionId));
    } catch (error) {
      if (!isPermissionDeniedError(error)) throw error;
      console.warn(
        "Skipping owned collection delete due to permissions:",
        collectionId,
      );
    }
  }

  // Trash, never destroy — and do not swallow failures here. Rules now block
  // client deletes outright, so a swallowed permission error would mean
  // silently leaving this business's capsules live after deletion. Storage
  // media is retained so the capsules can be restored intact.
  // capsuleType is pinned for the same reason as the link lookups above — the
  // list rule cannot see studio membership, so a businessId-only sweep is
  // denied for a non-admin owner. Admins keep the unfiltered sweep, which
  // already resolves through the rule's isAdmin() branch.
  const businessCapsulesQuery = query(
    collection(db, "timeCapsules"),
    where("businessId", "==", normalizedBusinessId),
  );
  const businessTimeCapsulesSnap = await getDocs(
    isAdminActor
      ? businessCapsulesQuery
      : query(
        businessCapsulesQuery,
        where("capsuleType", "in", NON_PRIVATE_CAPSULE_TYPES),
      ),
  );
  for (const capsuleDoc of businessTimeCapsulesSnap.docs) {
    await deleteTimeCapsule(capsuleDoc.id, {
      actor: { uid, isAdmin: isAdminActor },
      reason: "business_deleted",
      surface: "businessCascade",
    });
  }

  await archiveBusinessCampaigns({
    businessId: normalizedBusinessId,
    uid,
  });
  await deactivateBusinessTvDevices({
    businessId: normalizedBusinessId,
    uid,
  });
  await deactivateBusinessLocations({
    businessId: normalizedBusinessId,
    uid,
  });
  // Pairing-session cancellation moved server-side: functions/softDelete.js's
  // auditBusinessSoftDelete trigger cancels them from the Admin SDK once this
  // function's batch below flips the business to "deleted", so it cannot
  // half-happen the way a client-side sweep that swallowed permission errors
  // could.

  let membershipsSnap = { forEach: () => {} };
  let invitesSnap = { forEach: () => {} };
  try {
    membershipsSnap = await getDocs(
      query(
        collection(businessDb, "businessMemberships"),
        where("businessId", "==", normalizedBusinessId),
      ),
    );
  } catch (error) {
    if (!isPermissionDeniedError(error)) throw error;
    console.warn("Skipping membership lookup due to permissions.");
  }
  try {
    invitesSnap = await getDocs(
      query(
        collection(businessDb, "businessInvites"),
        where("businessId", "==", normalizedBusinessId),
      ),
    );
  } catch (error) {
    if (!isPermissionDeniedError(error)) throw error;
    console.warn("Skipping invite lookup due to permissions.");
  }

  const businessRef = doc(businessDb, "businesses", normalizedBusinessId);

  const businessBatch = writeBatch(businessDb);
  membershipsSnap.forEach((membershipDoc) => {
    businessBatch.set(
      membershipDoc.ref,
      {
        status: "inactive",
        deactivatedAt: serverTimestamp(),
        deactivatedByUid: uid,
        leftAt: serverTimestamp(),
        leftReason: "business_deleted",
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  });
  invitesSnap.forEach((inviteDoc) => {
    businessBatch.set(
      inviteDoc.ref,
      {
        status: "cancelled",
        cancelledAt: serverTimestamp(),
        cancelledByUid: uid,
        cancelReason: "business_deleted",
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  });
  businessBatch.set(
    businessRef,
    {
      status: "deleted",
      deletedAt: serverTimestamp(),
      deletedByUid: uid,
      // Clear the roster so a deleted business grants no member read access.
      memberUids: [],
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
  await businessBatch.commit();

  const affectedMemberships = membershipsSnap.docs.map((membershipDoc) => ({
    id: membershipDoc.id,
    ...(membershipDoc.data() || {}),
  }));
  const affectedUids = Array.from(
    new Set(
      affectedMemberships
        .map((membership) => String(membership?.uid || "").trim())
        .filter(Boolean),
    ),
  );
  if (normalizedUid && !affectedUids.includes(normalizedUid)) {
    affectedUids.push(normalizedUid);
  }

  const currentPrimaryByUid = new Map();
  const chunkSize = 10; // Firestore "in" query limit
  for (let i = 0; i < affectedUids.length; i += chunkSize) {
    const slice = affectedUids.slice(i, i + chunkSize);
    if (slice.length === 0) continue;
    const usersSnap = await getDocs(
      query(collection(db, "users"), where(documentId(), "in", slice)),
    );
    usersSnap.forEach((userDoc) => {
      currentPrimaryByUid.set(
        userDoc.id,
        String(userDoc.data()?.primaryBusinessId || "").trim() || null,
      );
    });
  }

  const uidsNeedingPrimaryUpdate = affectedUids.filter(
    (memberUid) =>
      String(currentPrimaryByUid.get(memberUid) || "").trim() ===
      normalizedBusinessId,
  );
  const userUpdates = (
    await Promise.all(
      uidsNeedingPrimaryUpdate.map(async (memberUid) => {
        if (memberUid === normalizedUid) {
          const normalizedNextPrimary =
            String(nextPrimaryBusinessId || "").trim() || null;
          return { uid: memberUid, nextPrimaryBusinessId: normalizedNextPrimary };
        }
        // Recomputing another member's next primary requires reading their
        // memberships. Once the business is soft-deleted above, the actor is no
        // longer an active member, so studio rules can deny that read. This is
        // best-effort bookkeeping — the member's primary pointer is reconciled
        // on their own next sign-in — so a permission error here must not fail
        // the delete that has already committed.
        try {
          const remainingMemberships =
            await listBusinessMembershipsForUser(memberUid);
          const ownerMembership = remainingMemberships.find(
            (membership) =>
              String(membership?.role || "")
                .trim()
                .toLowerCase() === "owner",
          );
          const resolvedNextPrimaryBusinessId =
            String(ownerMembership?.businessId || "").trim() ||
            String(remainingMemberships[0]?.businessId || "").trim() ||
            null;
          return {
            uid: memberUid,
            nextPrimaryBusinessId: resolvedNextPrimaryBusinessId,
          };
        } catch (error) {
          if (!isPermissionDeniedError(error)) throw error;
          console.warn(
            "Skipping member primary reassignment due to permissions:",
            memberUid,
          );
          return null;
        }
      }),
    )
  ).filter(Boolean);

  const maxWritesPerBatch = 450;
  for (let i = 0; i < userUpdates.length; i += maxWritesPerBatch) {
    const userBatch = writeBatch(db);
    const slice = userUpdates.slice(i, i + maxWritesPerBatch);
    slice.forEach(
      ({
        uid: memberUid,
        nextPrimaryBusinessId: resolvedNextPrimaryBusinessId,
      }) => {
        const patch = {
          primaryBusinessId: resolvedNextPrimaryBusinessId || null,
          updatedAt: serverTimestamp(),
        };
        if (memberUid === normalizedUid) {
          patch.onboardingStatus = resolvedNextPrimaryBusinessId
            ? ONBOARDING_STATUS.COMPLETED
            : ONBOARDING_STATUS.NOT_STARTED;
        }
        userBatch.set(doc(db, "users", memberUid), patch, { merge: true });
      },
    );
    await userBatch.commit();
  }

  return { ok: true };
};

/**
 * Give up the business account.
 *
 * Takes every business this user OWNS offline and returns them to an individual
 * account. It deliberately does no content cascade of its own: setting the
 * business status is enough, because syncBusinessContentOnStatusChange drafts
 * the content and can restore it if the business ever comes back. That leaves a
 * single code path capable of taking content offline.
 *
 * `businessId` is no longer read. It used to come from `primaryBusinessId`,
 * which means "the business I am viewing" — so a user who owned one business and
 * collaborated on another could take the collaborator's business offline.
 */
export const downgradeBusinessAccount = async ({ uid }) => {
  const normalizedUid = String(uid || "").trim();
  if (!normalizedUid) throw new Error("uid is required.");

  const allBusinesses = await listBusinessesForUser(normalizedUid);
  const ownedBusinesses = allBusinesses.filter((row) => {
    const ownerUid = String(row?.ownerUid || "").trim();
    const createdByUid = String(row?.createdByUid || "").trim();
    return ownerUid === normalizedUid || createdByUid === normalizedUid;
  });
  const ownedIds = new Set(ownedBusinesses.map((row) => row.id));

  // A single batch: either every owned business goes offline, or none does.
  // This must commit before the user-profile write below, so a failed batch
  // never leaves the profile marked as downgraded.
  if (ownedBusinesses.length > 0) {
    const businessBatch = writeBatch(businessDb);
    for (const business of ownedBusinesses) {
      businessBatch.set(
        doc(businessDb, "businesses", business.id),
        {
          status: "inactive",
          downgradedAt: serverTimestamp(),
          downgradedByUid: normalizedUid,
          updatedByUid: auditActorUid(normalizedUid),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    }
    await businessBatch.commit();
  }

  // A collaborator keeps working where they were; only owned businesses go.
  const nextPrimaryBusinessId =
    String(
      allBusinesses.find((row) => !ownedIds.has(row.id))?.id || "",
    ).trim() || null;

  const downgradedBusinessIds = Array.from(ownedIds);

  await setDoc(
    doc(db, "users", normalizedUid),
    {
      accountType: ACCOUNT_TYPES.INDIVIDUAL,
      onboardingStatus: ONBOARDING_STATUS.COMPLETED,
      primaryBusinessId: nextPrimaryBusinessId,
      // downgradedBusinessId (singular) is a legacy scalar kept for profiles
      // and readers written before a user could own more than one business.
      // downgradedBusinessIds is the source of truth going forward; readers
      // should check both (see assertCapsuleMutationAllowed).
      downgradedBusinessId: downgradedBusinessIds[0] || null,
      downgradedBusinessIds,
      downgradedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  return {
    ok: true,
    nextPrimaryBusinessId,
    downgradedBusinessIds,
  };
};

export const getUserSummariesByUids = async (uids = []) => {
  const unique = Array.from(
    new Set((uids || []).map((v) => String(v || "").trim()).filter(Boolean)),
  );
  if (unique.length === 0) return {};

  const out = {};
  const chunkSize = 10; // Firestore "in" query limit

  for (let i = 0; i < unique.length; i += chunkSize) {
    const slice = unique.slice(i, i + chunkSize);
    const q = query(collection(db, "users"), where(documentId(), "in", slice));
    const snap = await getDocs(q);
    snap.forEach((d) => {
      const data = d.data() || {};
      out[d.id] = {
        uid: d.id,
        displayName: data.displayName || "",
        userName: data.userName || "",
        email: data.email || "",
        profileImage: data.profileImage || data?.user?.profileImage || "",
      };
    });
  }

  return out;
};
